import type { ValidateFunction } from 'ajv';
import { prepareAjvErrors } from '../../utils';
import type {
  IConfigSource,
  GetValue,
  GetValueOption,
  GetValuesAll,
} from '../loader.types';

/**
 * Creates a configuration source from a local JSON **untrusted** source.
 * The JSON may be loaded from a local file, fetched from the network, or otherwise provided.
 *
 * Example:
 * ```
 * const source = new LocalJsonSource(validators.backend, JSON.parse(obj));
 * const config = new ConfigLoader(source);
 * ```
 */
export class LocalJsonSource<T> implements IConfigSource<T> {
  config: T | undefined;

  /**
   * @param defaults - The untrusted JSON object.
   * @param validator - The generated Ajv validator.
   */
  constructor(validator: ValidateFunction, defaults: unknown) {
    // validate will modify `untrusted`.
    if (!validator(defaults)) {
      const errors = prepareAjvErrors(validator);
      console.error(errors);
      throw new Error('config and schema mismatch');
    }

    this.config = defaults as T;
  }

  getValue: GetValue<T> = (key) => {
    const value = this.config?.[key];
    if (value === undefined) {
      throw new Error(`key not found: ${key.toString()}`);
    }

    return value;
  };

  getValueOptional: GetValueOption<T> = (key) => {
    return this.config?.[key];
  };

  getValuesAll: GetValuesAll<T> = () => this.config;
}
